import React, { useEffect, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import ReactDOM from 'react-dom/client';
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom';
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import I18nextBrowserLanguageDetector from 'i18next-browser-languagedetector';
import store from './app/store';
import { ConfirmationModalProvider } from './contexts/ConfirmationContext';
import UserService from '@services/UserService';
import { connectUser } from '@slices/userSlice';
import MainLayout from '@components/layouts/MainLayout';
import OfflineLayout from '@components/layouts/OfflineLayout';
import LoginPage from '@components/pages/LoginPage/LoginPage';
import TosPage from '@components/pages/TosPage/TosPage';
import LumebasPage from '@components/pages/LumebasPage/LumebasPage';
import DashboardPage from '@components/pages/DashboardPage/DashboardPage';
import GroupsPage from '@components/pages/GroupsPage/GroupsPage';
import ScenesPage from '@components/pages/ScenesPage/ScenesPage';
// import SubscriptionPage from '@components/pages/SubscriptionPage/SubscriptionPage';
import AccountPage from '@components/pages/AccountPage/AccountPage';
import SettingsPage from '@components/pages/SettingsPage/SettingsPage';
import LoadingPage from '@components/pages/LoadingPage/LoadingPage';
import ForgotPasswordPage from '@components/pages/ForgotPasswordPage/ForgotPasswordPage';
import ResetPasswordPage from '@components/pages/ResetPasswordPage/ResetPasswordPage';
import ChangeLogPage from '@components/pages/ChangeLogPage/ChangeLogPage';
import ExternalApisPage from '@components/pages/ExternalApisPage/ExternalApisPage';
import ScreenshotsPage from '@components/pages/ScreenshotsPage/ScreenshotsPage';
import translationEN from './locales/en.json';
import translationFR from './locales/fr.json';
import { TutorialModalProvider } from '@contexts/TutorialContext';
import './app.scss';

const resources = {
  en: {
    translation: translationEN
  },
  fr: {
    translation: translationFR
  },
};

i18n
.use(I18nextBrowserLanguageDetector)
.use(initReactI18next)
.init({
    resources,
    supportedLngs: ['en', 'fr'],
    fallbackLng: 'en',
    detection: {
        order: ['cookie', 'navigator'],
        caches: ['cookie'],
    },
    react: {
        useSuspense: false,
    },
    lng: navigator.language.startsWith('fr') ? 'fr' : 'en',
});

function App() {
    const [isLoading, setLoading] = useState<boolean>(true);

    const dispatch = useDispatch();
    const user = useSelector((state: any) => state.user);
    const { i18n } = useTranslation();

    useEffect(() => {
        const userService = new UserService();
        userService.me().then(data => {
            dispatch(connectUser(data.body));
            i18n.changeLanguage(data.body.language ?? 'en');
        }).catch(e => {

        }).finally(() => setLoading(false));
    }, []);

    return (
        <Routes>
            {isLoading ? (
                <Route path="/" element={<OfflineLayout />}>
                    <Route index element={<LoadingPage/>} />
                </Route>
            ) : user ? (
                <Route path="/" element={<MainLayout />}>
                    <Route index element={<LumebasPage/>} />
                    <Route path="m/:productId/dashboard" element={<DashboardPage/>} />
                    <Route path="m/:productId/groups" element={<GroupsPage/>} />
                    <Route path="m/:productId/groups/:groupId" element={<ScenesPage/>} />
                    {/* <Route path="subscription" element={<SubscriptionPage/>} /> */}
                    <Route path="screenshots" element={<ScreenshotsPage/>} />
                    <Route path="settings" element={<SettingsPage/>} />
                    <Route path="settings/account" element={<AccountPage/>} />
                    <Route path="settings/apis" element={<ExternalApisPage/>} />
                    {/* <Route path="settings/developer" element={<SettingsPage/>} /> */}
                    <Route path="changelog" element={<ChangeLogPage/>} />
                    <Route path="tos" element={<TosPage/>} />
                    <Route path="*" element={<Navigate replace to="/"/>} />
                </Route>
            ): (
                <Route path="/" element={<OfflineLayout />}>
                    <Route index element={<LoginPage/>} />
                    <Route path="tos" element={<TosPage/>} />
                    <Route path="forgot-password" element={<ForgotPasswordPage/>} />
                    <Route path="reset-password" element={<ResetPasswordPage/>} />
                    <Route path="*" element={<Navigate replace to="/"/>} />
                </Route>
            )}
        </Routes>
    );
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <BrowserRouter>
    <Provider store={store}>
        <ConfirmationModalProvider>
            <TutorialModalProvider>
                <App/>
            </TutorialModalProvider>
        </ConfirmationModalProvider>
    </Provider>
  </BrowserRouter>
);
